import { Box, Button, Tab, Tabs } from '@mui/material';
import { cloneDeep } from 'lodash';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import { FuelPortalService } from '@/services';
import { useAppDispatch } from '@/store';
import type { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { assetEditorSelector, CreatAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { startJob } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '@/utils/appUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';

import VNAccessSettings from '../VNAccessSettings/VNAccessSettings';
import VideoForm from '../VNAssetUpload/forms/video';
import { useModalContext } from '../VNModal/VNModalProvider';
import DisplaySettingsForm from './DisplaySettingsForm';
import Preview from './Preview';
import { toSaveAssetPayload } from './toSaveAssetPayload';

const VNAssetEditor = ({ addToList = null }) => {
  const { assetForm } = useSelector(assetEditorSelector);
  const { fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { ouData } = useSelector(applicationDataSelector);
  const { onClose } = useModalContext();
  const [selectionType, setSelectionType] = useState('');
  const { alert } = useAppUtils();
  const [isInitialized, setIsInitialized] = useState(false);
  const [tab, setTab] = useState('details');
  const { loading, setProgress } = useProgress();
  const dispatch = useAppDispatch();

  const methods = useForm({
    defaultValues: {} as AssetForm,
  });
  const { reset, getValues, setValue } = methods;

  const onSave = async () => {
    if (loading) {
      return;
    }
    const data = getValues();
    setProgress(true, 'Saving Asset...');
    try {
      const payloadRequest = toSaveAssetPayload(data, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
      // console.log('data', { data, payloadRequest });
      const response = await FuelPortalService.updateAsset(payloadRequest);
      if (response && addToList) {
        addToList(response);
        onClose();
        alert('Asset successfully saved.');
      }
    } catch (e) {
      console.error('Unable to save asset', { e });
    } finally {
      setProgress(false);
    }
  };
  const onCancel = () => {
    onClose();
  };

  // const isSuperAdmin = useMemo(() => permissions && permissions.has(Permission.OUSuperAdmin), [permissions]);
  // const schemaType = watch('schemaType');

  const base = 'body';
  const expanded = 'body expanded';

  const handleTabChange = async (event: SyntheticEvent, value: string) => {
    setTab(value);
  };

  useEffect(() => {
    if (assetForm) {
      reset(assetForm);
      setSelectionType(assetForm.schemaType === SchemaType.Video ? 'video' : 'image');
      setProgress(false);
      setIsInitialized(true);
    } else {
      setProgress(true);
    }
  }, [assetForm]);

  if (!isInitialized) {
    return null;
  }

  // check MoviedEdit for an updated version
  const onUploadThumbnail = async (file: File) => {
    const allowedFiles = ['.JPEG', '.jpeg', '.JPG', '.jpg', '.PNG', '.png', '.GIF', '.gif'];
    const regex = new RegExp(`([\\W\\S])+(${allowedFiles.join('|')})$`);
    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    if (file && !regex.test(file && file.name.toLowerCase())) {
      alert(bodyText, 'error');
      return;
    }

    const imgBlob = await cliptoolUtils.getFileImageBlob(file);

    const frozenForm: AssetForm = cloneDeep(assetForm);
    frozenForm.ingestFile = file;
    // console.log('onUploadThumbnail', { frozenForm });
    dispatch(CreatAssetForm(frozenForm));
    await dispatch(startJob({ form: frozenForm, jobType: 'Thumbnail' }));

    // optimistic update
    setValue('originalAssetData.data.thumbnail.tempThumbnailBlob', imgBlob);
  };

  return (
    <div className="vn-asset-editor">
      <div className="fullHeight">
        <div className="tab-header-container">
          <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={handleTabChange} aria-label="series tabs">
              <Tab label="Details" value="details" />
              <Tab label="Access" value="access-schedule" />
              {/* <Tab label="Share" value="share" /> */}
            </Tabs>
          </Box>
        </div>
        <FormProvider {...methods}>
          <div className="tab-body-container">
            {tab === 'details' && (
              <div className={expanded}>
                <div className="column left">
                  {/* <VNAssetForm /> */}
                  <Preview videoId="player1" type={selectionType as 'video' | 'image'} />
                  <VideoForm isMultiple={false} />
                </div>
                <div className="column right">
                  <DisplaySettingsForm type={selectionType as 'video' | 'image'} uploadThumbnail={onUploadThumbnail} />
                </div>
              </div>
            )}
            {tab === 'access-schedule' && <VNAccessSettings name="accessSettings" />}
          </div>

          <div className="actions">
            <div className="end-actions">
              <Button variant="contained" sx={{ mr: 1 }} onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={onSave}>
                Save
              </Button>
            </div>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default VNAssetEditor;
